import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Users, CheckCircle, Clock, RefreshCw, Loader2,
  Fingerprint, MessageSquare, UserCheck, FileText
} from 'lucide-react';

const PROGRAM_LABELS = { heat: 'HEAT', her: 'HER', youth: 'HEAT for Youth' };
const PROGRAM_COLORS = {
  heat: { bg: 'bg-orange-600', light: 'bg-orange-50', text: 'text-orange-700', accent: 'border-orange-500' },
  her: { bg: 'bg-pink-600', light: 'bg-pink-50', text: 'text-pink-700', accent: 'border-pink-500' },
  youth: { bg: 'bg-violet-600', light: 'bg-violet-50', text: 'text-violet-700', accent: 'border-violet-500' },
};

export default function FacilitatorLiveDashboard({ program = 'heat' }) {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const colors = PROGRAM_COLORS[program] || PROGRAM_COLORS.heat;
  const label = PROGRAM_LABELS[program] || 'HEAT';

  const [session, setSession] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadSession = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [sessionRes, modsRes] = await Promise.all([
        api.get(`/class/session/live/${user.id}?program=${program}`).catch(() => ({ data: { session: null, attendees: [], count: 0 } })),
        api.get(`/class/modules?program=${program}`).catch(() => ({ data: [] })),
      ]);
      setSession(sessionRes.data.session);
      setAttendees(sessionRes.data.attendees || []);
      setModules(modsRes.data || []);
    } catch {
      toast.error('Failed to load session');
    }
    setLoading(false);
    setRefreshing(false);
  }, [program, user.id]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => loadSession(), 15000);
    return () => clearInterval(interval);
  }, [loadSession]);

  const startSession = async () => {
    if (!selectedModule) return toast.error('Select a module first');
    try {
      const res = await api.post(`/class/session/start?program=${program}&module_id=${selectedModule}`);
      if (res.data?.id) {
        toast.success('Session started! Participants can now sign in.');
        await loadSession();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start session');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  return (
    <div className="space-y-6" data-testid="facilitator-live-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Users className={colors.text} size={22} /> {label} Live Class
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {session ? `Session active — ${new Date().toLocaleDateString()}` : 'No active session today'}
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full gap-1" onClick={() => loadSession(true)} disabled={refreshing} data-testid="refresh-btn">
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </Button>
      </div>

      {/* No Active Session */}
      {!session && (
        <Card className={`border-0 shadow-sm rounded-2xl border-t-4 ${colors.accent}`}>
          <CardContent className="p-6 space-y-4">
            <div className="text-center">
              <Fingerprint size={40} className={`${colors.text} mx-auto mb-3 opacity-60`} />
              <h2 className="font-semibold">Start Today's Session</h2>
              <p className="text-sm text-muted-foreground mt-1">Select the module you're teaching and participants can sign in.</p>
            </div>
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger className="rounded-xl h-12" data-testid="start-session-module-select">
                <SelectValue placeholder="Select today's module" />
              </SelectTrigger>
              <SelectContent>
                {modules.map(m => <SelectItem key={m.id} value={m.id}>{m.name}{m.section ? ` (${m.section})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={startSession} disabled={!selectedModule} className={`w-full ${colors.bg} hover:opacity-90 text-white rounded-full h-12 gap-2`} data-testid="start-session-btn">
              <Users size={16} /> Open Session for Sign-In
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Session */}
      {session && (
        <>
          {/* Session Info */}
          <Card className={`border-0 shadow-sm rounded-2xl ${colors.light}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">{session.module_name || 'Session Active'}</p>
                  <p className="text-xs text-muted-foreground">{session.date} &middot; {session.module_section && `${session.module_section} Section`}</p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${colors.text}`}>{attendees.length}</p>
                  <p className="text-xs text-muted-foreground">Signed In</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-3 text-center">
                <UserCheck size={18} className="mx-auto mb-1 text-emerald-600" />
                <p className="text-lg font-bold">{attendees.length}</p>
                <p className="text-[10px] text-muted-foreground">Present</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-3 text-center">
                <MessageSquare size={18} className="mx-auto mb-1 text-violet-600" />
                <p className="text-lg font-bold">{attendees.filter(a => a.reflection_submitted).length}</p>
                <p className="text-[10px] text-muted-foreground">Reflected</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-3 text-center">
                <Clock size={18} className="mx-auto mb-1 text-amber-600" />
                <p className="text-lg font-bold">{attendees.filter(a => !a.reflection_submitted).length}</p>
                <p className="text-[10px] text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
          </div>

          {/* Attendee List */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users size={14} /> Attendance ({attendees.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {attendees.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Fingerprint size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Waiting for participants to sign in...</p>
                  <p className="text-xs mt-1">They'll appear here as they enter their PIN</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {attendees.map((att, idx) => (
                    <div key={att.user_id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-testid={`attendee-row-${att.user_id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${colors.light} ${colors.text}`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{att.full_name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            Signed in: {new Date(att.signed_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {att.reflection_submitted ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] gap-0.5">
                            <CheckCircle size={10} /> Reflected
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] gap-0.5">
                            <Clock size={10} /> Pending
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4 flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full gap-1 text-xs" onClick={() => window.location.href = `/${program}/session-docs`} data-testid="go-to-session-docs">
                <FileText size={12} /> Write Session Documentation
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
